import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { nextBusinessDay, ymdLocal, formatBR } from "@/lib/holidays";
import { ExerciseImage } from "@/components/ExerciseImage";
import {
  ChevronLeft, ChevronRight, Download, Loader2, CalendarX, CheckCircle2, XCircle,
  RefreshCw, ChevronDown, BarChart3, CalendarClock, AlertTriangle,
  Pencil, Trash2, ArrowRightLeft, Check, Image as ImageIcon,
} from "lucide-react";

const ABSENCE_ALERT = 3;
const MUSCLE_GROUPS = ["Peito", "Costas", "Pernas", "Ombro", "Bíceps", "Tríceps", "Antebraço", "Glúteo", "Panturrilha", "Core/Abdômen"];
const imageSrc = (v) => (v ? (v.startsWith("http") ? v : `${process.env.REACT_APP_BACKEND_URL}/api/photo/${v}`) : null);

const WEEKDAY_LABELS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const STATUSES = [
  { k: "presente", label: "Presente", icon: CheckCircle2, cls: "bg-emerald-500 text-[#0A0D12]", ring: "border-emerald-500 text-emerald-400" },
  { k: "faltou", label: "Faltou", icon: XCircle, cls: "bg-red-500 text-white", ring: "border-red-500 text-red-400" },
  { k: "reagendou", label: "Reagendou", icon: RefreshCw, cls: "bg-amber-500 text-[#0A0D12]", ring: "border-amber-500 text-amber-400" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AgendaDia({ refreshKey }) {
  const [view, setView] = useState("day"); // day | report
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [report, setReport] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [editItem, setEditItem] = useState(null); // {booking, index, sets, reps, load_note}
  const [subItem, setSubItem] = useState(null); // {booking, index}
  const [subGroup, setSubGroup] = useState("todos");
  const [delItem, setDelItem] = useState(null); // {booking, index}
  const [viewEx, setViewEx] = useState(null); // { name, ex }
  const [resched, setResched] = useState(null); // { booking, date, suggested }

  useEffect(() => { api.get("/exercises").then((r) => setExercises(r.data)).catch(() => {}); }, []);

  const saveItems = async (booking, items) => {
    const workout = { ...(booking.workout || {}), items };
    try {
      await api.put(`/agenda/${booking.id}`, { workout });
      toast.success("Treino atualizado!");
      loadDay();
    } catch (_) { toast.error("Falha ao atualizar treino"); }
  };

  const applyEdit = async () => {
    const items = editItem.booking.workout.items.map((it, i) =>
      i === editItem.index ? { ...it, sets: editItem.sets, reps: editItem.reps, load_note: editItem.load_note } : it);
    await saveItems(editItem.booking, items);
    setEditItem(null);
  };

  const confirmDelItem = async () => {
    const items = delItem.booking.workout.items.filter((_, i) => i !== delItem.index);
    await saveItems(delItem.booking, items);
    setDelItem(null);
  };

  const applySubstitute = async (ex) => {
    const items = subItem.booking.workout.items.map((it, i) =>
      i === subItem.index ? { ...it, exercise_id: ex.id, exercise_name: ex.name, muscle_group: ex.muscle_group, image_url: ex.image_url } : it);
    await saveItems(subItem.booking, items);
    setSubItem(null);
  };

  const openExercise = async (item) => {
    const name = typeof item === "string" ? item : item.exercise_name;
    const snapshot = typeof item === "object" && item ? item.image_url : null;
    let list = exercises;
    try { const { data } = await api.get("/exercises"); list = data; setExercises(data); } catch (_) {}
    const norm = (s) => (s || "").trim().toLowerCase();
    const ex = list.find((e) => norm(e.name) === norm(name)) || null;
    setViewEx({ name, ex, image_url: (ex && ex.image_url) || snapshot || null });
  };
  const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
  const [repStart, setRepStart] = useState(monthStart());
  const [repEnd, setRepEnd] = useState(todayISO());

  const reqId = useRef(0);
  const loadDay = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const res = await api.get("/agenda/day", { params: { date } });
      if (id === reqId.current) setData(res.data);
    } catch (_) {
      if (id === reqId.current) toast.error("Falha ao carregar o dia");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [date]);

  const loadReport = useCallback(async () => {
    try {
      const res = await api.get("/agenda/report", { params: { start: repStart, end: repEnd } });
      setReport(res.data);
    } catch (_) {
      toast.error("Falha ao carregar relatório");
    }
  }, [repStart, repEnd]);

  useEffect(() => { if (view === "day") loadDay(); }, [view, loadDay, refreshKey]);
  useEffect(() => { if (view === "report") loadReport(); }, [view, loadReport, refreshKey]);

  const shiftDate = (days) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().slice(0, 10));
  };

  const setStatus = async (booking, status) => {
    if (status === "reagendou" && !booking.is_makeup) {
      const sug = nextBusinessDay(new Date(date + "T12:00:00"));
      setResched({ booking, date: ymdLocal(sug), suggested: formatBR(sug) });
      return;
    }
    try {
      if (booking.is_makeup) await api.patch(`/agenda/${booking.id}/makeup`, { status, date });
      else await api.patch("/agenda/attendance", { booking_id: booking.id, date, status });
      setData((prev) => ({ ...prev, bookings: prev.bookings.map((b) => b.id === booking.id && !!b.is_makeup === !!booking.is_makeup ? { ...b, attendance_status: status } : b) }));
      toast.success("Presença registrada");
    } catch (_) {
      toast.error("Falha ao registrar presença");
    }
  };

  const doReschedule = async () => {
    if (!resched?.date) { toast.error("Escolha a nova data"); return; }
    try {
      await api.patch(`/agenda/${resched.booking.id}/reschedule`, { new_date: resched.date, original_date: date });
      toast.success("Reposição criada — este dia ficou como Faltou.");
      setResched(null); loadDay();
    } catch (e) { toast.error("Falha ao reagendar"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/agenda/day/pdf", { params: { date }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `agenda-${date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF gerado!");
    } catch (_) {
      toast.error("Falha ao gerar PDF");
    } finally {
      setDownloading(false);
    }
  };

  const grouped = {};
  (data?.bookings || []).forEach((b) => { (grouped[b.time] = grouped[b.time] || []).push(b); });

  const wd = data ? WEEKDAY_LABELS[data.weekday] : "";

  return (
    <div>
      <div className="flex gap-2 mb-5 bg-[#181F2C] border border-[#263147] rounded-2xl p-1.5 w-full sm:w-auto sm:inline-flex">
        <button data-testid="dia-view-day" onClick={() => setView("day")}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-sm font-medium transition-colors ${view === "day" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"}`}>
          <CalendarClock className="w-4 h-4" /> Agenda do dia
        </button>
        <button data-testid="dia-view-report" onClick={() => setView("report")}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-sm font-medium transition-colors ${view === "report" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"}`}>
          <BarChart3 className="w-4 h-4" /> Relatório de frequência
        </button>
      </div>

      {view === "day" ? (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between bg-[#181F2C] border border-[#263147] rounded-2xl p-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" data-testid="dia-prev" onClick={() => shiftDate(-1)} className="h-11 w-11 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl"><ChevronLeft className="w-4 h-4" /></Button>
              <Input type="date" data-testid="dia-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl w-44" />
              <Button variant="outline" size="icon" data-testid="dia-next" onClick={() => shiftDate(1)} className="h-11 w-11 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl"><ChevronRight className="w-4 h-4" /></Button>
              {wd && <span className="text-sm text-slate-400 ml-1">{wd}</span>}
            </div>
            <Button data-testid="dia-download-pdf" onClick={downloadPdf} disabled={downloading || data?.is_weekend}
              className="h-11 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl px-5">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />} Baixar PDF
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-emerald-500 animate-spin" /></div>
          ) : data?.is_weekend ? (
            <div className="text-center py-16 text-slate-500"><CalendarX className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>Sem agenda no fim de semana.</p></div>
          ) : (data?.bookings?.length === 0) ? (
            <div className="text-center py-16 text-slate-500"><CalendarClock className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>Nenhum treino agendado para este dia.</p></div>
          ) : (
            <div className="space-y-4" data-testid="dia-list">
              {Object.keys(grouped).sort().map((time) => (
                <div key={time}>
                  <h3 className="text-sm font-semibold text-emerald-400 mb-2">{time}</h3>
                  <div className="space-y-2">
                    {grouped[time].map((b) => {
                      const rk = b.is_makeup ? `makeup-${b.id}` : b.id;
                      const goldName = (b.is_makeup && !b.attendance_status) || (!b.is_makeup && b.attendance_status === "reagendou");
                      return (
                      <div key={rk} data-testid={`dia-booking-${rk}`} className={`bg-[#181F2C] border rounded-2xl overflow-hidden ${b.is_makeup ? "border-dashed" : "border-[#263147]"}`} style={b.is_makeup ? { borderColor: goldName ? "#FFD700" : "#263147" } : undefined}>
                        <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                          <button onClick={() => setExpanded(expanded === rk ? null : rk)} className="flex items-center gap-2 flex-1 text-left flex-wrap" data-testid={`dia-expand-${rk}`}>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expanded === rk ? "rotate-180" : ""}`} />
                            <span data-testid={`dia-student-name-${rk}`} className="font-semibold text-slate-100"
                              style={goldName ? { color: "#FFD700" } : undefined}>{b.student_name}</span>
                            {b.student_nickname && <span className="text-sm text-slate-500">"{b.student_nickname}"</span>}
                            {b.is_encaixe && <Badge className="bg-red-500/15 text-red-400 border-red-500/30">Encaixe</Badge>}
                            {b.is_makeup && <Badge data-testid={`dia-makeup-badge-${b.id}`} className="bg-amber-500/10 border-amber-500/30" style={{ color: "#FFD700" }}><RefreshCw className="w-3 h-3 mr-1" />Reposição{b.makeup_original_date ? ` de ${b.makeup_original_date.split("-").reverse().join("/")}` : ""}</Badge>}
                            {!b.is_makeup && b.rescheduled_to && b.attendance_status === "faltou" && <Badge data-testid={`dia-rescheduled-to-${b.id}`} className="bg-slate-500/15 text-slate-400 border-slate-600/40">Reposição em {b.rescheduled_to.split("-").reverse().join("/")}</Badge>}
                          </button>
                          <div className="flex gap-1.5">
                            {STATUSES.filter((s) => !(b.is_makeup && s.k === "reagendou")).map((s) => {
                              const active = b.attendance_status === s.k;
                              const Icon = s.icon;
                              return (
                                <button key={s.k} data-testid={`attendance-${s.k}-${rk}`} onClick={() => setStatus(b, s.k)}
                                  className={`h-9 px-3 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5 ${active ? s.cls + " border-transparent" : "bg-[#0F1520] border-[#263147] " + s.ring.split(" ")[1] + " hover:bg-[#1E2738]"}`}>
                                  <Icon className="w-3.5 h-3.5" /> {s.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {expanded === rk && (
                          <div className="px-4 pb-4 border-t border-[#263147] pt-3">
                            {b.workout?.model_name && <p className="text-xs text-emerald-400 mb-2">Modelo: {b.workout.model_name}</p>}
                            {b.workout?.type === "manual" ? (
                              <p className="text-sm text-slate-300 whitespace-pre-wrap">{b.workout.manual_text || "—"}</p>
                            ) : b.workout?.items?.length ? (
                              <div className="space-y-1.5">
                                {b.workout.items.map((it, i) => (
                                  <div key={i} className="flex items-center gap-2 text-sm bg-[#0F1520] border border-[#263147] rounded-lg px-2.5 py-2" data-testid={`dia-item-${b.id}-${i}`}>
                                    <span className="text-slate-600 text-xs w-4">{i + 1}.</span>
                                    <button data-testid={`dia-item-name-${b.id}-${i}`} onClick={() => openExercise(it)}
                                      className="flex-1 text-left text-slate-200 truncate hover:text-emerald-400 transition-colors underline decoration-dotted decoration-slate-600 underline-offset-2">{it.exercise_name}</button>
                                    <span className="text-xs text-slate-400 hidden sm:inline">{[it.sets && `${it.sets} séries`, it.reps && `${it.reps} reps`, it.load_note].filter(Boolean).join(" · ")}</span>
                                    <div className="flex gap-1 shrink-0">
                                      <button data-testid={`dia-item-edit-${b.id}-${i}`} onClick={() => setEditItem({ booking: b, index: i, sets: it.sets || "", reps: it.reps || "", load_note: it.load_note || "" })}
                                        className="w-7 h-7 rounded-md bg-[#181F2C] border border-[#263147] text-slate-300 hover:text-emerald-400 hover:border-emerald-500/40 flex items-center justify-center" title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                                      <button data-testid={`dia-item-substitute-${b.id}-${i}`} onClick={() => { setSubItem({ booking: b, index: i }); setSubGroup("todos"); }}
                                        className="w-7 h-7 rounded-md bg-[#181F2C] border border-[#263147] text-slate-300 hover:text-blue-400 hover:border-blue-500/40 flex items-center justify-center" title="Substituir"><ArrowRightLeft className="w-3.5 h-3.5" /></button>
                                      <button data-testid={`dia-item-delete-${b.id}-${i}`} onClick={() => setDelItem({ booking: b, index: i })}
                                        className="w-7 h-7 rounded-md bg-[#181F2C] border border-[#263147] text-slate-300 hover:text-red-400 hover:border-red-500/40 flex items-center justify-center" title="Excluir"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : <p className="text-sm text-slate-500">Sem treino definido.</p>}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="report-section">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end bg-[#181F2C] border border-[#263147] rounded-2xl p-4">
            <div>
              <label className="text-xs text-slate-400 block mb-1">De</label>
              <Input type="date" data-testid="report-start-input" value={repStart} onChange={(e) => setRepStart(e.target.value)} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl w-44" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Até</label>
              <Input type="date" data-testid="report-end-input" value={repEnd} onChange={(e) => setRepEnd(e.target.value)} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl w-44" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="report-preset-month" onClick={() => { setRepStart(monthStart()); setRepEnd(todayISO()); }} className="h-11 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Este mês</Button>
              <Button variant="outline" data-testid="report-preset-all" onClick={() => { setRepStart(""); setRepEnd(""); }} className="h-11 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Tudo</Button>
            </div>
          </div>

          <div className="bg-[#181F2C] border border-[#263147] rounded-2xl overflow-hidden" data-testid="report-table">
          {report.filter((r) => r.absence_streak >= ABSENCE_ALERT).length > 0 && (
            <div data-testid="absence-alert-banner" className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border-b border-red-500/30 px-4 py-3">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {report.filter((r) => r.absence_streak >= ABSENCE_ALERT).length} aluno(s) com {ABSENCE_ALERT}+ faltas seguidas — atenção!
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-[#0F1520] text-xs uppercase tracking-wider text-slate-400">
                  <th className="text-left px-4 py-3">Aluno</th>
                  <th className="text-center px-3 py-3">Presenças</th>
                  <th className="text-center px-3 py-3">Faltas</th>
                  <th className="text-center px-3 py-3">Reagendou</th>
                  <th className="text-center px-3 py-3">Frequência</th>
                  <th className="text-center px-3 py-3">Faltas seguidas</th>
                </tr>
              </thead>
              <tbody>
                {report.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-500">Nenhum aluno ativo.</td></tr>
                ) : report.map((r) => {
                  const alert = r.absence_streak >= ABSENCE_ALERT;
                  return (
                  <tr key={r.student_id} data-testid={`report-row-${r.student_id}`} className={`border-t border-[#263147] ${alert ? "bg-red-500/5" : ""}`}>
                    <td className={`px-4 py-3 ${alert ? "border-l-2 border-l-red-500" : ""}`}><span className="font-medium text-slate-100">{r.name}</span>{r.nickname && <span className="text-slate-500 text-sm"> "{r.nickname}"</span>}</td>
                    <td className="text-center text-emerald-400 font-semibold">{r.presente}</td>
                    <td className="text-center text-red-400 font-semibold">{r.faltou}</td>
                    <td className="text-center text-amber-400 font-semibold">{r.reagendou}</td>
                    <td className="text-center">
                      <span className={`text-sm font-semibold ${r.freq_pct >= 75 ? "text-emerald-400" : r.freq_pct >= 50 ? "text-amber-400" : "text-red-400"}`}>{r.total ? `${r.freq_pct}%` : "—"}</span>
                    </td>
                    <td className="text-center">
                      {r.absence_streak > 0 ? (
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${alert ? "bg-red-500/20 text-red-300 border border-red-500/40" : "bg-slate-500/15 text-slate-400 border border-slate-600/40"}`}>
                          {alert && <AlertTriangle className="w-3 h-3" />}{r.absence_streak}x
                        </span>
                      ) : <span className="text-slate-600 text-sm">—</span>}
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      )}

      {/* Exercise demo (image/GIF from Library) */}
      <Dialog open={!!viewEx} onOpenChange={(o) => !o && setViewEx(null)}>
        <DialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl max-w-md" data-testid="exercise-view-modal">
          {viewEx && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{viewEx.name}</DialogTitle>
                {viewEx.ex?.muscle_group && <DialogDescription className="text-slate-400">{viewEx.ex.muscle_group}{viewEx.ex.equipment ? ` • ${viewEx.ex.equipment}` : ""}</DialogDescription>}
              </DialogHeader>
              <div className="py-1">
                <div className="w-full rounded-xl bg-[#0F1520] border border-[#263147] overflow-hidden flex items-center justify-center" style={{ minHeight: 200 }}>
                  {imageSrc(viewEx.image_url) ? (
                    <ExerciseImage value={viewEx.image_url} alt={viewEx.name} minHeight={200} testId="exercise-view-image" className="w-full object-contain max-h-[60vh]" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-12 text-slate-600"><ImageIcon className="w-10 h-10" /><span className="text-xs">Sem imagem/GIF cadastrado na Biblioteca</span></div>
                  )}
                </div>
                {viewEx.ex?.description && <p className="text-sm text-slate-400 mt-3">{viewEx.ex.description}</p>}
              </div>
              <DialogFooter>
                <Button data-testid="exercise-view-close" onClick={() => setViewEx(null)} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl">Fechar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule (creates makeup class; this day becomes Faltou) */}
      <Dialog open={!!resched} onOpenChange={(o) => !o && setResched(null)}>
        <DialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl max-w-sm" data-testid="dia-reschedule-modal">
          {resched && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2"><RefreshCw className="w-5 h-5" style={{ color: "#FFD700" }} /> Reagendar aula</DialogTitle>
                <DialogDescription className="text-slate-400">{resched.booking.student_name} — este dia será marcado como Faltou.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-1">
                <div data-testid="dia-reschedule-suggested" className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 text-sm" style={{ color: "#FFD700" }}>
                  <CalendarClock className="w-4 h-4 shrink-0" />
                  <span>Data sugerida: <span className="font-semibold">{resched.suggested}</span> — próximo dia útil</span>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Nova data</Label>
                  <Input type="date" data-testid="dia-reschedule-date-input" value={resched.date} onChange={(e) => setResched((r) => ({ ...r, date: e.target.value }))} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" data-testid="dia-reschedule-cancel" onClick={() => setResched(null)} className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Cancelar</Button>
                <Button data-testid="dia-reschedule-save" onClick={doReschedule} className="text-[#0A0D12] font-semibold rounded-xl hover:opacity-90" style={{ backgroundColor: "#FFD700" }}>Reagendar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit item (only this class) */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl max-w-sm">
          {editItem && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2"><Pencil className="w-5 h-5 text-emerald-400" /> Editar exercício</DialogTitle>
                <DialogDescription className="text-slate-400">{editItem.booking.workout.items[editItem.index]?.exercise_name} — apenas nesta aula.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-3 py-2">
                <div><Label className="text-xs text-slate-400">Séries</Label><Input data-testid="edit-item-sets" value={editItem.sets} onChange={(e) => setEditItem((p) => ({ ...p, sets: e.target.value }))} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl mt-1" /></div>
                <div><Label className="text-xs text-slate-400">Repetições</Label><Input data-testid="edit-item-reps" value={editItem.reps} onChange={(e) => setEditItem((p) => ({ ...p, reps: e.target.value }))} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl mt-1" /></div>
                <div><Label className="text-xs text-slate-400">Carga/Obs.</Label><Input data-testid="edit-item-load" value={editItem.load_note} onChange={(e) => setEditItem((p) => ({ ...p, load_note: e.target.value }))} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl mt-1" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditItem(null)} className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Cancelar</Button>
                <Button data-testid="edit-item-save" onClick={applyEdit} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl">Salvar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Substitute exercise (keeps sets/reps/load) */}
      <Dialog open={!!subItem} onOpenChange={(o) => !o && setSubItem(null)}>
        <DialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 [&>*]:min-w-0">
          {subItem && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-blue-400" /> Substituir exercício</DialogTitle>
                <DialogDescription className="text-slate-400">Escolha o novo exercício — séries, repetições e carga são mantidos.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {["todos", ...MUSCLE_GROUPS].map((g) => (
                  <button key={g} type="button" data-testid={`sub-group-${g}`} onClick={() => setSubGroup(g)}
                    className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${subGroup === g ? "bg-emerald-500 text-[#0A0D12] border-emerald-500" : "bg-[#0F1520] text-slate-300 border-[#263147] hover:border-[#34435f]"}`}>{g === "todos" ? "Todos" : g}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 min-w-0" data-testid="sub-cards">
                {(subGroup === "todos" ? exercises : exercises.filter((e) => e.muscle_group === subGroup)).map((ex) => {
                  const img = imageSrc(ex.image_url);
                  return (
                    <button key={ex.id} type="button" data-testid={`sub-card-${ex.id}`} onClick={() => applySubstitute(ex)}
                      className="min-w-0 text-left rounded-xl border border-[#263147] bg-[#0F1520] p-2 hover:border-emerald-500/50 transition-all">
                      <div className="w-full h-20 rounded-lg bg-[#181F2C] border border-[#263147] flex items-center justify-center mb-2 overflow-hidden">
                        {img ? <ExerciseImage value={ex.image_url} alt={ex.name} className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-700" />}
                      </div>
                      <p className="text-xs font-medium text-slate-100 leading-tight line-clamp-2">{ex.name}</p>
                      <span className="text-[10px] text-slate-500">{ex.muscle_group}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete item from this class */}
      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Excluir exercício desta aula?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {delItem && `"${delItem.booking.workout.items[delItem.index]?.exercise_name}" será removido apenas do treino desta aula. A Biblioteca não é afetada.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-item" onClick={confirmDelItem} className="bg-red-500 hover:bg-red-600 text-white rounded-xl">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
